import { config, passport } from '@imtbl/sdk';

// Get values from environment variables with fallbacks
const PUBLISHABLE_KEY = import.meta.env.VITE_IMMUTABLE_PUBLISHABLE_KEY || 'pk_imapik-test-LadUEl5YXgqcXY5RwzV8';
const CLIENT_ID = import.meta.env.VITE_IMMUTABLE_CLIENT_ID || 'SDKOGboXNjPluzWsUjbvhOjIpsOlf3TJ';

// Determine the environment (SANDBOX for testnet, PRODUCTION for mainnet)
const ENVIRONMENT = import.meta.env.VITE_IMMUTABLE_ENVIRONMENT === 'production'
  ? config.Environment.PRODUCTION
  : config.Environment.SANDBOX;

console.log('Immutable Passport Configuration:');
console.log('Client ID:', CLIENT_ID);
console.log('Publishable Key:', PUBLISHABLE_KEY);
console.log('Environment:', ENVIRONMENT === config.Environment.PRODUCTION ? 'Production' : 'Sandbox');

// Base configuration for Immutable SDK
const baseConfig = {
  environment: ENVIRONMENT,
  publishableKey: PUBLISHABLE_KEY,
};

// Force app.bitsport.gg URLs for production environment
// This ensures we always use the URLs registered in the Immutable Hub
console.log('Using app.bitsport.gg environment for Passport');

// Hardcode the redirect URIs to the app.bitsport.gg domain
const redirectUri = 'https://app.bitsport.gg/passport-callback';
const logoutRedirectUri = 'https://app.bitsport.gg/passport-logout';

// Log the current hostname for debugging
console.log('Current hostname:', window.location.hostname);
console.log('Using fixed redirect URIs for app.bitsport.gg');

// We're using hardcoded values, so we don't need to override with environment variables
// But we'll log if environment variables are set for debugging purposes
if (import.meta.env.VITE_IMMUTABLE_REDIRECT_URI) {
  console.log('Note: VITE_IMMUTABLE_REDIRECT_URI is set but not used:', import.meta.env.VITE_IMMUTABLE_REDIRECT_URI);
}

if (import.meta.env.VITE_IMMUTABLE_LOGOUT_REDIRECT_URI) {
  console.log('Note: VITE_IMMUTABLE_LOGOUT_REDIRECT_URI is set but not used:', import.meta.env.VITE_IMMUTABLE_LOGOUT_REDIRECT_URI);
}

console.log('Current hostname:', window.location.hostname);
console.log('Using redirect URI:', redirectUri);
console.log('Using logout redirect URI:', logoutRedirectUri);

// Create Passport instance with dynamic redirect URLs based on environment
export const passportInstance = new passport.Passport({
  baseConfig,
  clientId: CLIENT_ID,
  redirectUri: redirectUri, // Use the redirect URI we determined above
  logoutRedirectUri: logoutRedirectUri, // Use the logout redirect URI we determined above
  audience: 'platform_api',
  scope: 'openid offline_access email transact',
  popupOverlayOptions: {
    disableGenericPopupOverlay: false,
    disableBlockedPopupOverlay: false,
  }
});

/**
 * Function to get the Passport instance
 * This ensures that components can access the same instance
 */
export const getPassportInstance = () => {
  return passportInstance;
};

// Log the redirect URLs for debugging
console.log('Current environment: Production (app.bitsport.gg)');
console.log('Hostname:', window.location.hostname);
console.log('Origin:', window.location.origin);
console.log('Client ID:', CLIENT_ID);
console.log('Environment:', ENVIRONMENT === config.Environment.PRODUCTION ? 'Production' : 'Sandbox');

/**
 * Ensure AppKit doesn't interfere with Passport
 * This function should be called before any Passport operations
 */
const ensurePassportProvider = () => {
  // Mark that we're using Passport
  localStorage.setItem('wallet_provider', 'passport');

  // Clear any AppKit auto-reconnect flags
  localStorage.removeItem('wallet_reconnect_requested');

  // Don't remove other wallet data as it might be needed for the session
};

/**
 * Helper function to connect to Passport EVM provider
 * This establishes the connection to the Immutable blockchain
 */
export const connectPassportProvider = async () => {
  try {
    // Ensure we're using Passport
    ensurePassportProvider();

    console.log('Connecting to Passport EVM provider...');
    // Connect to the Passport EVM provider
    const provider = await passportInstance.connectEvm();
    console.log('Passport EVM provider connected successfully');

    // Add custom error handling for the provider
    const originalRequest = provider.request;
    provider.request = async (args) => {
      try {
        return await originalRequest.call(provider, args);
      } catch (error) {
        // Handle network-related errors
        if (error.message && error.message.includes('network')) {
          console.error('Network error in Passport provider:', error);
          // Don't let AppKit try to handle this
          error.isPassportError = true;
          // Provide a more helpful error message
          throw new Error(`Immutable Passport network error: ${error.message}. Please ensure you're connected to the correct network.`);
        }
        throw error;
      }
    };

    return provider;
  } catch (error) {
    console.error('Error connecting to Passport provider:', error);
    // Add more context to the error
    if (error.message && error.message.includes('popup')) {
      throw new Error(`Passport login popup was blocked. Please allow popups for this site and try again. Original error: ${error.message}`);
    }
    throw error;
  }
};

/**
 * Helper function to login with Passport
 * This handles the full authentication flow
 */
export const loginWithPassport = async () => {
  try {
    // Ensure we're using Passport
    ensurePassportProvider();

    console.log('Starting Passport login process...');

    // First check if user is already logged in
    try {
      const existingUserInfo = await passportInstance.getUserInfo();
      if (existingUserInfo) {
        console.log('User already logged in with Passport:', existingUserInfo);

        // Connect to provider to get the address
        console.log('Connecting to existing Passport provider...');
        const provider = await connectPassportProvider();
        console.log('Provider connected:', provider);

        console.log('Requesting accounts...');
        const accounts = await provider.request({ method: 'eth_requestAccounts' });
        console.log('Accounts received:', accounts);

        // Store user info in localStorage for persistence
        if (accounts && accounts.length > 0) {
          const address = accounts[0];
          localStorage.setItem('passport_user_info', JSON.stringify(existingUserInfo));
          localStorage.setItem('passport_address', address);
        }

        return { provider, accounts, userInfo: existingUserInfo };
      }
    } catch (existingUserError) {
      console.log('No existing Passport session found, proceeding with login');
    }

    console.log('Connecting to Passport provider...');
    const provider = await connectPassportProvider();
    console.log('Provider connected:', provider);

    console.log('Requesting accounts...');
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    console.log('Accounts received:', accounts);

    // Try to get user info
    let userInfo = null;
    try {
      userInfo = await passportInstance.getUserInfo();
      console.log('User info:', userInfo);

      // Store user info in localStorage for persistence
      if (userInfo && accounts && accounts.length > 0) {
        const address = accounts[0];
        localStorage.setItem('passport_user_info', JSON.stringify(userInfo));
        localStorage.setItem('passport_address', address);
      }
    } catch (userInfoError) {
      console.warn('Could not get user info:', userInfoError);
    }

    return { provider, accounts, userInfo };
  } catch (error) {
    console.error('Error logging in with Passport:', error);
    if (error.message) {
      console.error('Error message:', error.message);
    }
    if (error.stack) {
      console.error('Error stack:', error.stack);
    }

    // If there's a network error, provide a more helpful message
    if (error.message && error.message.includes('network')) {
      console.error('Network error detected. This might be due to an incompatible network configuration.');
      // Add a custom property to the error to help identify it
      error.isNetworkError = true;
      throw new Error(`Network error connecting to Immutable. Please ensure you're connected to the correct network. Original error: ${error.message}`);
    }

    throw error;
  }
};

/**
 * Helper function to get user info from Passport
 * This can be used to check if the user is logged in
 */
export const getPassportUserInfo = async () => {
  try {
    // Ensure we're using Passport
    ensurePassportProvider();

    const userInfo = await passportInstance.getUserInfo();
    return userInfo;
  } catch (error) {
    console.error('Error getting user info from Passport:', error);
    return null;
  }
};

/**
 * Helper function to logout from Passport
 * This handles the full logout flow
 */
export const logoutFromPassport = async () => {
  try {
    console.log('Logging out from Passport...');

    // Store the current URL to detect navigation loops
    const initialUrl = window.location.href;
    let hasRedirected = false;

    // Clear Passport-specific localStorage items first
    localStorage.removeItem('passport_user_info');
    localStorage.removeItem('passport_address');
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Only remove wallet_provider if it's set to passport
    if (localStorage.getItem('wallet_provider') === 'passport') {
      localStorage.removeItem('wallet_provider');
    }

    // Clear any reconnect flags
    localStorage.removeItem('wallet_reconnect_requested');
    localStorage.removeItem('wallet_connected');

    try {
      // Create a flag to track if we're in a logout process
      // This will help prevent infinite redirects
      sessionStorage.setItem('passport_logout_in_progress', 'true');

      // Set up a listener to detect navigation loops
      const navigationWatcher = () => {
        // If we've already redirected once and we're back at the same URL,
        // we might be in a loop - abort the logout process
        if (hasRedirected && window.location.href === initialUrl) {
          console.warn('Detected potential navigation loop during Passport logout, aborting');
          sessionStorage.removeItem('passport_logout_in_progress');
          window.removeEventListener('focus', navigationWatcher);
          return;
        }
        hasRedirected = true;
      };

      // Listen for window focus events which happen after redirects
      window.addEventListener('focus', navigationWatcher);

      // Instead of using the SDK's logout which can hang, we'll manually clear the session
      // and redirect to the logout URL directly
      try {
        // Get the logout URL from the Passport instance
        const logoutUrl = passportInstance.getLogoutUrl();
        console.log('Passport logout URL:', logoutUrl);

        // Set a timeout to redirect to the logout URL
        setTimeout(() => {
          // Clean up the navigation watcher
          window.removeEventListener('focus', navigationWatcher);
          sessionStorage.removeItem('passport_logout_in_progress');

          // Redirect to the logout URL
          window.location.href = logoutUrl;
        }, 100);

        // Return early since we're redirecting
        return true;
      } catch (redirectError) {
        console.warn('Error getting logout URL, falling back to SDK logout:', redirectError);

        // Fall back to the SDK's logout method
        const logoutPromise = passportInstance.logout();

        // Set a timeout for the logout operation
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            window.removeEventListener('focus', navigationWatcher);
            sessionStorage.removeItem('passport_logout_in_progress');
            reject(new Error('Logout timeout'));
          }, 5000);
        });

        // Race the logout against the timeout
        await Promise.race([logoutPromise, timeoutPromise]);
        console.log('Passport logout successful');
      }

      // Clean up the navigation watcher
      window.removeEventListener('focus', navigationWatcher);
      sessionStorage.removeItem('passport_logout_in_progress');
    } catch (logoutError) {
      // If logout fails or times out, log the error but continue with cleanup
      console.warn('Passport logout encountered an issue:', logoutError);
      console.log('Continuing with local cleanup...');
      sessionStorage.removeItem('passport_logout_in_progress');
    }

    // Ensure we clear any remaining Passport session data
    try {
      // Clear any Passport cookies or session data that might be causing issues
      document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        if (name.includes('passport') || name.includes('imtbl')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
        }
      });
    } catch (cookieError) {
      console.warn('Error clearing cookies:', cookieError);
    }

    return true;
  } catch (error) {
    console.error('Error in Passport logout process:', error);
    sessionStorage.removeItem('passport_logout_in_progress');
    return false;
  }
};
